import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideMousePointerClick } from '@ng-icons/lucide';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmIconImports } from '@spartan-ng/helm/icon';

import { Router } from '@angular/router';
import { ListItem } from '@shared/components/list-item/list-item.component';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { Cartographie } from '../../cartographie/models/cartographie.model';

import { HlmResizableImports } from '@spartan-ng/helm/resizable';
import { AptitudeStore } from '../référentiel-métier-processus/processus-aptitude/store/aptitude.store';
import { AptitudeItemComponent } from './components/aptitude-item/aptitude-item.component';
import { RessourcesProfessionnellesFormComponent } from './components/ressources-professionnelles-form/ressources-professionnelles-form.component';
import { RessourcesProfessionnellesListComponent } from './components/ressources-professionnelles-list/ressources-professionnelles-list.component';
import { RéférentielMétierRessourcesProfessionnellesService } from './référentiel-métier-ressources-professionnelles.service';
import { ProductionStore } from './store/production.store';

@Component({
  selector: 'app-referentiel-metier-ressources-professionnelles',
  imports: [
    HlmSeparatorImports,
    HlmCardImports,
    HlmButtonImports,
    HlmIconImports,
    ListItem,
    ChipComponent,
    EmptyStateComponent,
    AptitudeItemComponent,
    RessourcesProfessionnellesListComponent,
    HlmResizableImports,
  ],
  templateUrl: './référentiel-métier-ressources-professionnelles.component.html',
  providers: [provideIcons({ lucideArrowLeft, lucideMousePointerClick })],
})
export class RéférentielMétierRessourcesProfessionnellesComponent implements OnInit {
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #productionStore = inject(ProductionStore);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #ressourceProfessionnelleService = inject(RéférentielMétierRessourcesProfessionnellesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #canEditService = inject(CanEditService);
  readonly #estVerrouilleService = inject(EstVerrouilleService);
  readonly #router = inject(Router);

  readonly cartographie = input.required<Cartographie>();
  readonly roles = input.required<string[]>();
  readonly idProduction = input<string>();

  readonly referentiel = computed(() => this.#referentielStore.selectedEntity());
  // Lue depuis ProductionStore (rempli à chaque chargement des aptitudes) pour refléter les modifications locales, comme le réordonnancement.
  readonly productionSelected = computed(() => {
    const idProduction = this.idProduction();
    return idProduction ? this.#productionStore.entityMap()[idProduction] : undefined;
  });

  readonly peutModifier = computed(() =>
    this.#canEditService.check(
      this.referentiel()?.equipeResponsable,
      this.roles(),
      LivrablePrincipal.REFERENTIEL_METIER,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  readonly estVerrouille = computed(() =>
    this.#estVerrouilleService.check(LivrablePrincipal.REFERENTIEL_METIER, this.cartographie(), this.referentiel()),
  );

  readonly aptitudes = linkedSignal(() => this.#aptitudeStore.entities());

  constructor() {
    effect(() => {
      const referentiel = this.referentiel();
      if (referentiel) {
        this.#aptitudeStore.charger({
          idCartographie: this.cartographie().id,
          idRéférentiel: referentiel.id,
          next: (aptitudes) =>
            this.#productionStore.chargerProductions({
              productions: aptitudes.filter((x) => x.productions).flatMap((x) => x.productions!),
            }),
        });
      }
    });
  }

  ngOnInit(): void {
    const referentiel = this.referentiel();
    if (!referentiel) return;

    this.#ressourceProfessionnelleService.refreshData.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() =>
      this.#aptitudeStore.charger({
        idCartographie: this.cartographie().id,
        idRéférentiel: referentiel.id,
        next: (aptitudes) =>
          this.#productionStore.chargerProductions({
            productions: aptitudes.filter((x) => x.productions).flatMap((x) => x.productions!),
          }),
      }),
    );
  }

  createRessource() {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une nouvelle ressource professionnelle',
      RessourcesProfessionnellesFormComponent,
      `Renseignez des ressources professionnelles et définissez à quelles productions elles sont rattachées.`,
      { cartographie: this.cartographie() },
    );
    this.#sidepanelService.open();
  }

  navigateToProcessus() {
    const segments = this.#router.url.split('/');
    segments.pop();
    segments.push('processus');
    this.#router.navigateByUrl(segments.join('/'), { replaceUrl: true });
  }
}
